import type { CSSProperties } from "react";

interface CollapseStyleOptions {
    maxHeight?: number;
    /**
     * When true, the consumer owns the *expanded* max-height via its own
     * className (e.g. `max-h-10 md:max-h-13` for a responsive cap).
     * collapseStyle doesn't bake the expanded max-height inline — it only
     * writes `max-height: 0` when collapsed. Mutually exclusive with
     * `maxHeight` (pass one or the other).
     */
    maxHeightFromClass?: boolean;
    marginTop?: number;
    marginBottom?: number;
    /**
     * When true, the consumer owns the *expanded* margin-bottom via its own
     * className (e.g. `mb-4 md:mb-5` for responsive spacing). collapseStyle
     * doesn't bake the expanded margin inline — it only writes
     * `margin-bottom: 0` when collapsed and includes `margin-bottom` in the
     * transition. Mutually exclusive with `marginBottom` (pass one or the
     * other).
     */
    marginBottomFromClass?: boolean;
    /** Negative margin-top applied when collapsed to eat a parent flex gap */
    gapCompensation?: number;
    /**
     * When true, the consumer owns the *collapsed* gap-eating margin via
     * its own className (e.g. `${collapsed ? "-mt-3 md:-mt-6" : ""}` for a
     * responsive parent gap of `gap-3 md:gap-6`). collapseStyle doesn't
     * bake the negative margin inline — it only includes `margin-top` in
     * the transition so the className-driven value animates smoothly.
     * Mutually exclusive with `gapCompensation` and `marginTop`.
     */
    gapCompensationFromClass?: boolean;
    /**
     * When true, vertical padding (top + bottom) transitions to 0 alongside
     * maxHeight. Useful for collapsing a whole section including its interior
     * spacing. Relies on `padding-top`/`padding-bottom` being set via class or
     * inline style elsewhere — we only override them to 0 when collapsed.
     */
    collapsePadding?: boolean;
    duration?: number;
    opacityDuration?: number;
}

/**
 * Returns a CSSProperties object that collapses or expands an element using
 * maxHeight + opacity transitions. Supports margin transitions, flex gap
 * compensation via negative margin-top, and optional vertical padding collapse.
 *
 * @param collapsed - Whether the element should be collapsed (hidden)
 * @param opts - Transition options
 *
 * @example
 * ```tsx
 * <div style={collapseStyle(isPending, { maxHeight: 200, marginBottom: 22.5, duration: 600 })}>
 *     <Content />
 * </div>
 *
 * // Collapse an entire section including its padding
 * <div
 *     className="px-6 pt-6 pb-2"
 *     style={collapseStyle(isInputting, { maxHeight: 500, collapsePadding: true })}
 * >
 *     <HeroSection />
 * </div>
 * ```
 */
export function collapseStyle(collapsed: boolean, opts: CollapseStyleOptions = {}): CSSProperties {
    const {
        maxHeight = 200,
        maxHeightFromClass = false,
        marginTop,
        marginBottom,
        marginBottomFromClass = false,
        gapCompensation,
        gapCompensationFromClass = false,
        collapsePadding = false,
        duration = 400,
        opacityDuration = duration
    } = opts;
    // Support gapCompensation and marginTop independently OR together.
    // Together: collapsed → -gapCompensation (eats parent's flex gap so the
    // mounted-but-collapsed slot contributes 0 layout); expanded → marginTop
    // (provides the desired spacing on top of the parent's gap). Used by the
    // stake/unstake/withdraw receipt slots, which mount inside a bordered
    // card whose flex gap would otherwise jump the card's min-content height
    // by gap-px the instant the receipt mounts.
    //
    // `gapCompensationFromClass` short-circuits this — caller's className
    // owns the negative margin (so it can be responsive, e.g.
    // `${collapsed ? "-mt-3 md:-mt-6" : ""}` matching a `gap-3 md:gap-6`
    // parent), and we just emit the transition. No inline marginTop.
    const effectiveMarginTop = gapCompensationFromClass
        ? undefined
        : gapCompensation !== undefined || marginTop !== undefined
          ? collapsed
              ? -(gapCompensation ?? 0)
              : (marginTop ?? 0)
          : undefined;
    const includeMarginBottomTransition = marginBottom !== undefined || marginBottomFromClass;
    const includeMarginTopTransition =
        marginTop !== undefined || gapCompensation !== undefined || gapCompensationFromClass;
    const parts = [
        `max-height ${duration}ms ease-out`,
        `opacity ${opacityDuration}ms ease-out`,
        includeMarginTopTransition ? `margin-top ${duration}ms ease-out` : "",
        includeMarginBottomTransition ? `margin-bottom ${duration}ms ease-out` : "",
        collapsePadding ? `padding ${duration}ms ease-out` : ""
    ].filter(Boolean);
    return {
        // maxHeightFromClass mirrors marginBottomFromClass: caller's className
        // sets the expanded cap (e.g. `max-h-10 md:max-h-13`); we only write
        // 0 inline when collapsed so the transition runs to and from the
        // className-driven value.
        ...(maxHeightFromClass ? collapsed && { maxHeight: 0 } : { maxHeight: collapsed ? 0 : maxHeight }),
        opacity: collapsed ? 0 : 1,
        overflow: "hidden" as const,
        ...(effectiveMarginTop !== undefined && { marginTop: effectiveMarginTop }),
        ...(marginBottom !== undefined && { marginBottom: collapsed ? 0 : marginBottom }),
        // marginBottomFromClass: only apply the inline override when collapsed
        // so the className wins in the expanded state. Override to 0 collapses
        // the spacing alongside the maxHeight transition.
        ...(marginBottomFromClass && collapsed && { marginBottom: 0 }),
        ...(collapsePadding && collapsed && { paddingTop: 0, paddingBottom: 0 }),
        transition: parts.join(", ")
    };
}
